import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { SilentRequest } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { AuthTokenService } from '@services/auth-token.service';


@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private readonly msalService: MsalService, private readonly authTokenService: AuthTokenService
  ) {}

   intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      // Skip token acquisition for local asset requests (images, icons, i18n, etc.)
      if (this.isAssetRequest(req.url)) {
        return next.handle(req);
      }

      const isGraph = this.authTokenService.isGraphApiUrl(req.url);
      const scopes = isGraph
        ? environment.msalConfig.auth.scopes
        : environment.msalConfig.auth.apiscopes;
  
      const silentRequest: SilentRequest = {
        scopes: scopes,
        account: this.msalService.instance.getActiveAccount() || undefined,
      };
  
      const scopeKey = this.authTokenService.getScopeKey(scopes);
      const cached = this.authTokenService.getToken(scopeKey, silentRequest, isGraph);
  
      return cached.pipe(
        switchMap((token) => {
          const tokenToUse = token || this.authTokenService.getTokenFromStorage(isGraph);
          if (tokenToUse) {
            return next.handle(this.addHeaders(req, tokenToUse, isGraph));
          }
          return next.handle(req);
        }),
        catchError((err) => {
          console.warn('Token acquisition failed:', err);
          const tokenToUse = this.authTokenService.getTokenFromStorage(isGraph);
          if (tokenToUse) {
            return next.handle(this.addHeaders(req, tokenToUse, isGraph));
          }
          return next.handle(req);
        })
      );
    } 

  private addHeaders(req: HttpRequest<any>, token: string, isGraph: boolean): HttpRequest<any> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'X-TenantId': `${this.authTokenService.getProjectId()}`,
      'X-Module': `${this.authTokenService.getActiveModule()}`,
    };
    // Role is for backend audit/telemetry only; backend must still authorize from the token.
    if (!isGraph) {
      const role = this.authTokenService.getUserRole();
      if (role) {
        headers['X-UserRole'] = role;
      }
    }
    return req.clone({ setHeaders: headers });
  }

  private isAssetRequest(url: string): boolean {
    // Match relative asset paths and common static file extensions
    return (
      url.startsWith('assets/') ||
      url.startsWith('/assets/') ||
      url.startsWith('./assets/') ||
      /\.(svg|png|jpg|jpeg|gif|ico|woff2?|ttf|eot)$/i.test(url)
    );
  }
}